import React from 'react'
import { styled } from '@mui/system'
import { Input as BaseInput } from '@mui/base/Input'
import { Box, Typography } from '@mui/material'

/* ---------- estilo compartilhado ---------- */
const errorColor = '#f44336'

/* cada dígito do OTP */
const InputElement = styled('input')(
  ({ error }) => `
  width: 100%;             /* ocupa toda a célula da grid */
  max-width: 60px;         /* não cresce demais em telas largas */
  height: 46px;
  flex: 1 1 0;             /* caso use flexbox */
  font-family: integral-cf-bold, Jost, 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 8px 0;
  border-radius: 8px;
  text-align: center;
  background: ${error ? '#FFEBEE' : '#F0F0F0'};
  border: 1px solid ${error ? errorColor : '#DAE2ED'};
  box-shadow: ${error ? `0 0 0 3px ${errorColor}33` : '0 2px 4px rgba(0,0,0,0.05)'};

  &:hover {
    border-color: ${error ? errorColor : '#FF7043'};
  }

  &:focus {
    border-color: ${error ? errorColor : '#FF7043'};
    box-shadow: ${error ? `0 0 0 3px ${errorColor}33` : '0 0 0 3px #FF8A65'};
  }

  /* firefox */
  &:focus-visible {
    outline: 0;
  }

  /* responsivo */
  @media (max-width: 360px) {
    height: 40px;
    font-size: 0.75rem;
    max-width: 45px;
  }
`,
)

/* ---------- componente interno ---------- */
function OTP({ length, value, onChange, gap, error }) {
  const inputRefs = React.useRef(new Array(length).fill(null))

  const focusInput = idx => inputRefs.current[idx]?.focus()
  const selectInput = idx => inputRefs.current[idx]?.select()

  const handleKeyDown = (e, idx) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault()
        idx > 0 && (focusInput(idx - 1), selectInput(idx - 1))
        break
      case 'ArrowRight':
        e.preventDefault()
        idx < length - 1 && (focusInput(idx + 1), selectInput(idx + 1))
        break
      case 'Backspace':
        e.preventDefault()
        onChange(prev => prev.slice(0, idx) + prev.slice(idx + 1))
        idx > 0 && (focusInput(idx - 1), selectInput(idx - 1))
        break
      case 'Delete':
        e.preventDefault()
        onChange(prev => prev.slice(0, idx) + prev.slice(idx + 1))
        break
      default:
        /* seta pra cima, baixo, espaço etc – ignoradas */
        break
    }
  }

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, '').slice(-1) // último dígito numérico
    let insertIdx = idx
    while (inputRefs.current[insertIdx]?.value && insertIdx < length - 1) {
      insertIdx++
    }
    onChange(prev => {
      const arr = prev.split('')
      arr[insertIdx] = val
      return arr.join('')
    })
    if (val && idx < length - 1) focusInput(idx + 1)
  }

  const handlePaste = (e, idx) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text/plain').replace(/\D/g, '')
    const otpArr = value.split('')
    for (let i = 0; i < pasted.length && idx + i < length; i++) {
      otpArr[idx + i] = pasted[i]
    }
    onChange(otpArr.join(''))
  }
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: `repeat(${length}, 1fr)`,
        gap,
      }}
    >
      {Array.from({ length }).map((_, i) => (
        <BaseInput
          key={i}
          slots={{ input: InputElement }}
          aria-label={`Dígito ${i + 1} do código`}
          slotProps={{
            input: {
              type: isMobile ? 'number' : 'text',
              inputMode: isMobile ? 'numeric' : 'text',
              pattern: '[0-9]*',
              maxLength: 1,
              ref: el => (inputRefs.current[i] = el),
              onKeyDown: e => handleKeyDown(e, i),
              onChange: e => handleChange(e, i),
              onPaste: e => handlePaste(e, i),
              value: value[i] || '',
              error,
            },
          }}
        />
      ))}
    </Box>
  )
}

/* ---------- componente exportado ---------- */
export default function OTPInput({ length = 6, gap = 1, onChange, error = false, helperText }) {
  const [otp, setOtp] = React.useState('')

  /* Propaga para o pai sempre que mudar */
  React.useEffect(() => {
    onChange('otpCode', otp)
  }, [otp])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <OTP length={length} value={otp} onChange={setOtp} gap={gap} error={error} />
      {helperText && (
        <Typography color={errorColor} align='center'>
          {helperText}
        </Typography>
      )}
    </Box>
  )
}
